// Every price-bearing series uses this scale, even when its axis is hidden on
// mobile. Axis visibility must never choose a different coordinate system.
export const CHARTING_PRICE_SCALE_ID = 'right';

export function layoutPriceLineLabels(items, height, labelHeight) {
    if (!Number.isFinite(height) || height < labelHeight || labelHeight <= 0) return [];
    const gap = labelHeight + 2;
    const half = labelHeight / 2;
    const sorted = items
        .filter(item => Number.isFinite(item.y) && item.y >= 0 && item.y <= height)
        .map(item => ({ ...item, labelY: Math.max(half, Math.min(height - half, item.y)) }))
        .sort((a, b) => a.y - b.y);

    // Move only the badges to avoid collisions; their price anchors never move.
    for (let index = 1; index < sorted.length; index += 1) {
        sorted[index].labelY = Math.max(sorted[index].labelY, sorted[index - 1].labelY + gap);
    }
    if (sorted.length) {
        sorted[sorted.length - 1].labelY = Math.min(sorted[sorted.length - 1].labelY, height - half);
        for (let index = sorted.length - 2; index >= 0; index -= 1) {
            sorted[index].labelY = Math.min(sorted[index].labelY, sorted[index + 1].labelY - gap);
        }
    }
    return sorted;
}

// Draw in the price series' own render cycle, not in a separate scale or a
// delayed DOM/RAF overlay. Vertical-axis gestures therefore update the labels
// in the very same frame as the bars and native horizontal price lines.
export class ChartingPriceLineLabels {
    constructor(formatPrice) {
        this.formatPrice = formatPrice;
        this.lines = [];
        this.items = [];
        this.showPrices = true;
        this.series = null;
        this.chart = null;
        this.requestUpdate = null;
        this.views = [{ zOrder: () => 'top', renderer: () => this }];
    }

    attached({ chart, series, requestUpdate }) {
        this.chart = chart;
        this.series = series;
        this.requestUpdate = requestUpdate;
        this.updateAllViews();
    }

    detached() {
        this.chart = null;
        this.series = null;
        this.requestUpdate = null;
        this.items = [];
    }

    paneViews() {
        return this.views;
    }

    setLines(lines, { showPrices = true } = {}) {
        this.lines = lines.map(line => ({ ...line }));
        this.showPrices = showPrices;
        this.updateAllViews();
        this.requestUpdate?.();
    }

    updateAllViews() {
        this.items = this.series ? this.lines.flatMap(line => {
            const y = this.series.priceToCoordinate(line.price);
            // In particular, null is NOT the top of the pane (Number(null)=0).
            if (y === null || !Number.isFinite(y)) return [];
            return [{
                price: line.price,
                y,
                text: [this.showPrices ? this.formatPrice(line.price) : '', line.title].filter(Boolean).join('  '),
                color: line.color,
                textColor: line.axisLabelTextColor || '#f8fafc'
            }];
        }) : [];
    }

    draw(target) {
        if (!this.chart || !this.items.length) return;
        const { fontSize, fontFamily } = this.chart.options().layout;
        const labelHeight = fontSize + 6;
        target.useMediaCoordinateSpace(({ context, mediaSize }) => {
            const labels = layoutPriceLineLabels(this.items, mediaSize.height, labelHeight);
            context.save();
            try {
                context.font = `${fontSize}px ${fontFamily}`;
                context.textBaseline = 'middle';
                context.textAlign = 'left';
                for (const label of labels) {
                    const width = Math.min(context.measureText(label.text).width + 10, mediaSize.width - 16);
                    if (width <= 10) continue;
                    const x = 4;
                    context.fillStyle = label.color;
                    context.fillRect(x, label.labelY - labelHeight / 2, width, labelHeight);
                    context.fillStyle = label.textColor;
                    context.fillText(label.text, x + 5, label.labelY, width - 10);
                    if (Math.abs(label.labelY - label.y) > 0.5) {
                        // A short leader makes collision-adjusted badges
                        // unambiguous without displacing the actual price line.
                        context.strokeStyle = label.color;
                        context.lineWidth = 1;
                        context.setLineDash([]);
                        context.beginPath();
                        context.moveTo(x + width, label.labelY);
                        context.lineTo(x + width + 6, label.y);
                        context.stroke();
                    }
                }
            } finally {
                context.restore();
            }
        });
    }
}
